import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, HelpCircle, Search, X } from 'lucide-react';
import {
  getFaqLanguage,
  SUPPORT_FAQ_CATEGORIES,
  type FaqLanguage,
} from './data/supportFaq';

interface LocalizedFaqItem {
  id: string;
  question: string;
  answer: string;
  searchText: string;
}

interface LocalizedFaqCategory {
  id: string;
  title: string;
  items: LocalizedFaqItem[];
}

const normalizeSearchText = (value: string) =>
  value
    .normalize('NFKC')
    .toLocaleLowerCase()
    .replace(/[\p{P}\p{S}\s]+/gu, '');

const isSubsequence = (needle: string, haystack: string) => {
  if (needle.length < 2 || needle.length > haystack.length) return false;
  let cursor = 0;
  for (let index = 0; index < haystack.length && cursor < needle.length; index += 1) {
    if (haystack[index] === needle[cursor]) cursor += 1;
  }
  return cursor === needle.length;
};

const matchesQuery = (searchText: string, query: string) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return true;
  if (searchText.includes(normalizedQuery)) return true;

  const terms = query
    .normalize('NFKC')
    .toLocaleLowerCase()
    .split(/[\p{P}\p{S}\s]+/gu)
    .map(normalizeSearchText)
    .filter(Boolean);

  if (terms.length > 1 && terms.every((term) => searchText.includes(term))) return true;
  return normalizedQuery.length >= 3 && isSubsequence(normalizedQuery, searchText);
};

const localizeCategories = (language: FaqLanguage): LocalizedFaqCategory[] =>
  SUPPORT_FAQ_CATEGORIES.map((category) => ({
    id: category.id,
    title: category.title[language],
    items: category.items.map((faqItem) => {
      const question = faqItem.question[language];
      const answer = faqItem.answer[language];
      return {
        id: faqItem.id,
        question,
        answer,
        searchText: normalizeSearchText(`${category.title[language]} ${question} ${answer}`),
      };
    }),
  }));

export const SupportFaq: React.FC = () => {
  const { t, i18n } = useTranslation();
  const language = getFaqLanguage(i18n.resolvedLanguage);
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(SUPPORT_FAQ_CATEGORIES[0].id);
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const deferredQuery = useDeferredValue(query);

  useEffect(() => {
    setQuery('');
    setActiveCategory(SUPPORT_FAQ_CATEGORIES[0].id);
    setOpenFaq(null);
  }, [language]);

  const categories = useMemo(() => localizeCategories(language), [language]);
  const filteredCategories = useMemo(
    () => categories.map((category) => ({
      ...category,
      items: category.items.filter((faqItem) => matchesQuery(faqItem.searchText, deferredQuery)),
    })),
    [categories, deferredQuery],
  );

  const hasQuery = normalizeSearchText(deferredQuery).length > 0;
  const currentCategoryHasResults = filteredCategories.some(
    (category) => category.id === activeCategory && category.items.length > 0,
  );
  const resolvedCategoryId = hasQuery && !currentCategoryHasResults
    ? filteredCategories.find((category) => category.items.length > 0)?.id ?? activeCategory
    : activeCategory;

  useEffect(() => {
    if (resolvedCategoryId !== activeCategory) {
      setActiveCategory(resolvedCategoryId);
      setOpenFaq(null);
    }
  }, [activeCategory, resolvedCategoryId]);

  const visibleCategories = useMemo(
    () => filteredCategories.filter(
      (category) => category.id === resolvedCategoryId && category.items.length > 0,
    ),
    [filteredCategories, resolvedCategoryId],
  );

  const resultCount = useMemo(
    () => visibleCategories.reduce((count, category) => count + category.items.length, 0),
    [visibleCategories],
  );

  const handleSearch = (value: string) => {
    setQuery(value);
  };

  return (
    <section id="faq" className="scroll-mt-20 overflow-hidden bg-[#111111] py-24 md:py-32">
      <div className="container mx-auto max-w-6xl px-6 md:px-12">
        <div className="flex flex-col gap-8 border-b border-white/[0.09] pb-10 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="mb-7 flex h-11 w-11 items-center justify-center rounded-full bg-brand-gold/10 text-brand-gold">
              <HelpCircle className="h-5 w-5" />
            </div>
            <h2 className="font-display text-3xl font-bold leading-tight md:text-4xl">
              {t('supportPage.faq.title')}
            </h2>
          </div>

          <label className="w-full max-w-[240px] shrink-0 md:text-right">
            <span className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-white/35">
              {t('supportPage.faq.selectModel')}
            </span>
            <select
              defaultValue="o1pro"
              aria-label={t('supportPage.faq.selectModel')}
              className="mt-2 w-full border-0 bg-transparent py-2 text-sm font-semibold tracking-[0.06em] text-brand-gold outline-none md:text-right"
            >
              <option value="o1pro" className="bg-[#111111] text-white">WYNDME O1 PRO</option>
            </select>
          </label>
        </div>

        <div className="mt-12 grid gap-12 lg:grid-cols-[minmax(190px,0.62fr)_minmax(0,2.38fr)] lg:gap-20">
          <aside className="min-w-0">
            <div className="lg:sticky lg:top-28">
              <p className="mb-5 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/30">
                {t('supportPage.faq.browseByTopic')}
              </p>
              <nav aria-label={t('supportPage.faq.browseByTopic')} className="flex gap-2 overflow-x-auto pb-2 lg:block lg:space-y-1 lg:overflow-visible lg:pb-0">
                {filteredCategories.map((category) => {
                  const hasCategoryResults = category.items.length > 0;
                  return (
                  <button
                    key={category.id}
                    type="button"
                    disabled={hasQuery && !hasCategoryResults}
                    onClick={() => {
                      setActiveCategory(category.id);
                      setOpenFaq(null);
                    }}
                    className={`group flex shrink-0 items-center gap-3 rounded-full px-4 py-2.5 text-left text-xs transition-all disabled:cursor-default disabled:opacity-25 lg:w-full lg:rounded-none lg:border-l lg:px-4 lg:py-3 ${
                      resolvedCategoryId === category.id
                        ? 'bg-brand-gold text-black lg:border-brand-gold lg:bg-transparent lg:text-brand-gold'
                        : 'bg-white/[0.045] text-white/45 hover:text-white lg:border-white/[0.08] lg:bg-transparent'
                    }`}
                  >
                    <span className="min-w-0 flex-1 whitespace-nowrap lg:whitespace-normal">
                      {category.title}
                    </span>
                    <span className="text-[10px] opacity-55">{category.items.length}</span>
                  </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          <div className="min-w-0">
            <div className="relative mb-7 max-w-xl overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.04] px-4 md:px-5">
              <div className="flex items-center gap-3">
                <Search className="h-4 w-4 shrink-0 text-brand-gold" />
                <label htmlFor="faq-search" className="sr-only">
                  {t('supportPage.faq.searchLabel')}
                </label>
                <input
                  id="faq-search"
                  type="search"
                  value={query}
                  onChange={(event) => handleSearch(event.target.value)}
                  placeholder={t('supportPage.faq.searchPlaceholder')}
                  autoComplete="off"
                  className="min-w-0 flex-1 bg-transparent py-3.5 text-sm text-white outline-none placeholder:text-white/30"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    aria-label={t('supportPage.faq.clearSearch')}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/[0.07] text-white/55 transition-colors hover:bg-white/[0.12] hover:text-white"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            <div className="mb-8 flex items-center justify-between gap-5">
              <p aria-live="polite" className="text-sm text-white/45">
                {t('supportPage.faq.resultCount', { count: resultCount })}
              </p>
              {query && (
                <span className="max-w-[55%] truncate text-xs text-brand-gold">
                  “{deferredQuery}”
                </span>
              )}
            </div>

            {visibleCategories.length === 0 ? (
              <div className="border-y border-white/[0.08] py-16 text-center md:py-24">
                <Search className="mx-auto h-7 w-7 text-white/20" />
                <h3 className="mt-6 text-xl font-semibold">{t('supportPage.faq.noResultsTitle')}</h3>
                <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-white/45">
                  {t('supportPage.faq.noResultsDesc')}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setQuery('');
                  }}
                  className="mt-7 text-sm font-semibold text-brand-gold underline decoration-brand-gold/30 underline-offset-4 hover:decoration-brand-gold"
                >
                  {t('supportPage.faq.clearSearch')}
                </button>
              </div>
            ) : (
              <div className="space-y-16">
                {visibleCategories.map((category) => (
                  <section key={category.id} aria-labelledby={`faq-${category.id}`}>
                    <div className="mb-4 flex items-end justify-between gap-6">
                      <h3 id={`faq-${category.id}`} className="text-lg font-semibold md:text-xl">
                        {category.title}
                      </h3>
                      <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/25">
                        {t('supportPage.faq.questionCount', { count: category.items.length })}
                      </span>
                    </div>

                    <div className="border-t border-white/[0.11]">
                      {category.items.map((faqItem, index) => {
                        const isOpen = openFaq === faqItem.id;
                        const answerId = `faq-answer-${faqItem.id}`;
                        return (
                          <article key={faqItem.id} className="border-b border-white/[0.09]">
                            <button
                              type="button"
                              onClick={() => setOpenFaq(isOpen ? null : faqItem.id)}
                              aria-expanded={isOpen}
                              aria-controls={answerId}
                              className="group flex w-full items-start gap-4 py-6 text-left md:gap-7 md:py-7"
                            >
                              <span className={`mt-0.5 w-7 shrink-0 font-mono text-[10px] tracking-[0.12em] transition-colors ${isOpen ? 'text-brand-gold' : 'text-white/25'}`}>
                                {String(index + 1).padStart(2, '0')}
                              </span>
                              <span className={`min-w-0 flex-1 text-sm font-medium leading-7 transition-colors md:text-base ${isOpen ? 'text-white' : 'text-white/80 group-hover:text-white'}`}>
                                {faqItem.question}
                              </span>
                              <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all ${isOpen ? 'bg-brand-gold text-black' : 'bg-white/[0.055] text-white/50 group-hover:bg-white/[0.1] group-hover:text-white'}`}>
                                <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                              </span>
                            </button>
                            <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                              <div id={answerId} aria-hidden={!isOpen} className="min-h-0 overflow-hidden">
                                <p className="pb-8 pl-11 pr-3 text-sm leading-7 text-white/52 md:pl-[3.75rem] md:pr-16 md:leading-8">
                                  {faqItem.answer}
                                </p>
                              </div>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
